import { baseApi } from "./baseApi";
const COLLECTION_BASE_URL = "/collection";

export const collectionApi = baseApi.injectEndpoints({
  tagTypes: ["Collection"],

  endpoints: (build) => ({
    addCollection: build.mutation({
      query: (body) => ({
        url: COLLECTION_BASE_URL,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "Collection", id: "LIST" }],
    }),

    listCollection: build.query({
      query: (params) => ({
        url: COLLECTION_BASE_URL,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Collection",
              id: _id,
            })),
            { type: "Collection", id: "LIST" },
          ]
          : [{ type: "Collection", id: "LIST" }],
    }),

    listCollectionForDriver: build.query({
      query: (params) => ({
        url: `${COLLECTION_BASE_URL}/listCollectionForDriver`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Collection",
              id: _id,
            })),
            { type: "Collection", id: "DRIVER_LIST" },
          ]
          : [{ type: "Collection", id: "DRIVER_LIST" }],
    }),

    listCollectionForContractor: build.query({
      query: (params) => ({
        url: `${COLLECTION_BASE_URL}/collectionlists`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result.data.map(({ _id }) => ({
              type: "Collection",
              id: _id,
            })),
            { type: "Collection", id: "LIST" },
          ]
          : [{ type: "Collection", id: "LIST" }],
    }),

    getDDLCollectionList: build.query({
      query: (params) => ({
        url: `${COLLECTION_BASE_URL}/ddl`,
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
            ...result?.data?.map(({ _id }) => ({
              type: "Collection",
              _id,
            })),
            { type: "Collection", id: "DDL" },
          ]
          : [{ type: "Collection", id: "DDL" }],
    }),

    getCollectionDetail: build.query({
      query: (shipmentId) => `${COLLECTION_BASE_URL}/${shipmentId}`,
      providesTags: (result, error, shipmentId) => [
        { type: "Collection", id: shipmentId },
      ],
    }),
    updateCollection: build.mutation({
      query: ({ _id, ...body }) => ({
        url: `${COLLECTION_BASE_URL}/${_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { _id: id }) => [
        { type: "Collection", id },
        { type: "Collection", id: "LIST" },
        { type: "Collection", id: "DRIVER_LIST" },
      ],
    }),
    deleteShipmentFulfillment: build.mutation({
      query: (shipmentId) => ({
        url: `${COLLECTION_BASE_URL}/${shipmentId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Collection", id: "LIST" },
        { type: "Collection", id: "DRIVER_LIST" },
      ],
    }),
    // Shipment Stops API
    listCollectionStops: build.query({
      query: ({ collectionId, page, limit, ...params }) => ({
        url: `${COLLECTION_BASE_URL}/${collectionId}/stops`,
        params,
      }),
      providesTags: (_result, _error, { collectionId }) => [
        { type: "Collection", id: `STOPS_${collectionId}` },
      ],
    }),

    addCollectionStop: build.mutation({
      query: ({ collectionId, ...body }) => ({
        url: `${COLLECTION_BASE_URL}/${collectionId}/stops`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { collectionId }) => [
        { type: "Collection", id: collectionId },
        { type: "Collection", id: `STOPS_${collectionId}` },
      ],
    }),

    updateCollectionStops: build.mutation({
      query: ({ collectionId, stopId, ...body }) => ({
        url: `${COLLECTION_BASE_URL}/${collectionId}/stops/${stopId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { collectionId }) => [
        { type: "Collection", id: collectionId },
      ],
    }),
    addCollectionNotes: build.mutation({
      query: (payload) => {
        const { collectionId, ...body } = payload;

        return {
          url: `${COLLECTION_BASE_URL}/${collectionId}/notes`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: (_result, _error, { collectionId }) => [
        { type: "Collection", id: collectionId },
        { type: "Collection", id: "LIST" },
      ],
    }),

    // Update Collection Note
    updateCollectionNotes: build.mutation({
      query: ({ collectionId, noteId, ...body }) => ({
        url: `${COLLECTION_BASE_URL}/${collectionId}/notes/${noteId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { collectionId }) => [
        { type: "Collection", id: collectionId },
        { type: "Collection", id: "LIST" },
      ],
    }),

    // List Collection Notes
    listCollectionNotes: build.query({
      query: ({ collectionId, page, limit }) => ({
        url: `${COLLECTION_BASE_URL}/${collectionId}/notes`,
        params: { page, limit },
      }),
      providesTags: (_result, _error, { collectionId }) => [
        { type: "Collection", id: collectionId },
        { type: "Collection", id: "LIST" },
      ],
    }),

    listShipmentsByCollectionId: build.query({
      query: ({ collectionId, page, limit }) => ({
        url: `${COLLECTION_BASE_URL}/${collectionId}/shipments`,
        params: { page, limit },
      }),
      providesTags: (_result, _error, { _id }) => [
        { type: "Collection", id: _id },
        { type: "Collection", id: "LIST" },
      ],
    }),

    getDdlVendorProfileForCollection: build.query({
      query: (params) => ({
        url: `${COLLECTION_BASE_URL}/ddlVendorProfileForCollection`,
        params,
      }),
      providesTags: (_result, _error, { _id }) => [
        { type: "Collection", id: _id },
        { type: "Collection", id: "LIST" },
      ],
    }),

    getDdlVendorCustomerPendingShipmentForCollection: build.query({
      query: (params) => ({
        url: `${COLLECTION_BASE_URL}/ddlVendorCustomerPendingShipmentForCollection`,
        params,
      }),
      providesTags: (_result, _error, { _id }) => [
        { type: "Collection", id: _id },
        { type: "Collection", id: "LIST" },
      ],
    }),
    getDdlVendorCustomerPendingShipmentForCollectionPerBranch: build.query({
      query: (params) => ({
        url: `${COLLECTION_BASE_URL}/ddlVendorCustomerPendingShipmentForCollectionPerBranch`,
        params,
      }),
      providesTags: (_result, _error, { _id }) => [
        { type: "Collection", id: _id },
        { type: "Collection", id: "LIST" },
      ],
    }),

    getDriverShipmentLoadingShipments: build.query({
      query: (params) => ({
        url: `${COLLECTION_BASE_URL}/driverShipmentLoadingShipments`,
        params,
      }),
      providesTags: (_result, _error, { _id }) => [
        { type: "Collection", id: _id },
        { type: "Collection", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListCollectionQuery,
  useLazyListCollectionForContractorQuery,
  useLazyListCollectionForDriverQuery,
  useLazyListCollectionQuery,
  useAddCollectionMutation,
  useGetCollectionDetailQuery,
  useUpdateCollectionMutation,
  useDeleteShipmentFulfillmentMutation,
  useLazyGetDDLCollectionListQuery,
  useLazyGetDdlVendorProfileForCollectionQuery,
  useGetDdlVendorProfileForCollectionQuery,
  useLazyGetDdlVendorCustomerPendingShipmentForCollectionQuery,
  useLazyGetDdlVendorCustomerPendingShipmentForCollectionPerBranchQuery,
  //shipment vehicle stops
  useLazyListCollectionStopsQuery,
  useAddCollectionStopMutation,
  useUpdateCollectionStopsMutation,

  useAddCollectionNotesMutation,
  useUpdateCollectionNotesMutation,
  useListCollectionNotesQuery,
  useLazyListCollectionNotesQuery,

  useListShipmentsByCollectionIdQuery,
  useLazyListShipmentsByCollectionIdQuery,
  useLazyGetDriverShipmentLoadingShipmentsQuery,
} = collectionApi;
